// Empty string = relative URLs (production via nginx proxy).
// Only fall back to direct backend URL if env var is truly undefined.
const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'https://localhost:8443';

// These UUIDs must match AuthService.DEFAULT_ORG_ID / DEFAULT_TENANT_ID in the backend
const DEFAULT_ORG_UUID = '550e8400-e29b-41d4-a716-446655440000';
const DEFAULT_TENANT_UUID = '550e8400-e29b-41d4-a716-446655440001';

const DEV_ACCOUNTS = [
  { id: 'u1', name: 'Alice Chen', firstName: 'Alice', lastName: 'Chen', email: 'alice.chen@corp.io', role: 'Super Admin', av: 'AC' },
  { id: 'u2', name: 'Bob Martinez', firstName: 'Bob', lastName: 'Martinez', email: 'bob.martinez@corp.io', role: 'Org Admin', av: 'BM' },
  { id: 'u3', name: 'Fatima Al-Rashid', firstName: 'Fatima', lastName: 'Al-Rashid', email: 'fatima.r@corp.io', role: 'Tenant Admin', av: 'FA' },
  { id: 'u4', name: 'Chen Wei', firstName: 'Chen', lastName: 'Wei', email: 'chen.wei@corp.io', role: 'Developer', av: 'CW' },
];

let _devMode = false;
// ── SEC-25 (2026-06-11) — JWT tokens NEVER persist to sessionStorage. ──
// The canonical auth path is the HttpOnly `agentlets_session` cookie set
// by the backend on login (G409). The cookie travels on every fetch via
// `credentials: 'include'` AND across full-page redirects (OAuth consent,
// browser refresh) by design — sessionStorage rehydration is no longer
// needed for the auth path. Tokens stored in sessionStorage were the
// XSS-theft vector flagged by the Wave-2 audit (SEC-25); even though the
// JS variables below still hold the token in memory for the lifetime of
// this page load, an XSS payload can no longer survive a refresh to
// steal the token from a JS-accessible store.
//
// One-time cleanup at module load: clear any pre-SEC-25 sessionStorage
// entries from prior deployments so a returning user doesn't carry a
// stale token in JS-reachable storage. Defensive — works even if a
// future code path accidentally re-introduces a sessionStorage read.
try {
  sessionStorage.removeItem('_authToken');
  sessionStorage.removeItem('_refreshToken');
  sessionStorage.removeItem('_tokenExpiresAt');
} catch (_) { /* sessionStorage unavailable (SSR / private-mode quirks) — safe to ignore */ }

let _authToken = null;
let _refreshToken = null;
let _tokenExpiresAt = null;
let _currentEmail = null; // Track email across login → MFA flow
let _onSessionExpired = null;
let _onTokenRefreshed = null;
// Single-flight guard for refreshAccessToken — holds the in-flight refresh
// promise so concurrent callers share ONE network refresh (see refreshAccessToken).
let _refreshInFlight = null;

/**
 * G590 — Extract a user-readable error message from a fetch Response that
 * came back non-OK. Handles three shapes:
 *   1. JSON with an `error` / `message` / `detail` field — return that.
 *   2. HTML body (e.g. nginx 502 page) — return a clean status-coded
 *      fallback instead of dumping the entire DOCTYPE into a toast.
 *   3. Plain-text non-empty body — return it (trimmed + capped at 240 chars
 *      so an accidentally large body still doesn't blow out the UI).
 *
 * Tries JSON first because that's the application contract; falls back to
 * text() only on parse failure. The `defaultMsg` is a per-call short
 * descriptor like "Switch failed" so the status-fallback reads naturally.
 */
async function _extractErrorMessage(res, defaultMsg) {
  const status = res?.status ?? 0;
  const statusFallback = `${defaultMsg || 'Request failed'} (${status || 'network'})`;
  // Try JSON first — same path apiFetch takes.
  try {
    const cloned = res.clone();
    const j = await cloned.json();
    const msg = j?.error || j?.message || j?.detail;
    if (msg && typeof msg === 'string') return msg.length > 240 ? msg.slice(0, 240) + '…' : msg;
  } catch (_) { /* not JSON — fall through */ }
  // Try text — but reject anything that looks like HTML (proxy / gateway pages).
  try {
    const t = (await res.text() || '').trim();
    if (!t) return statusFallback;
    // Heuristic: a stray angle bracket near the start means HTML / XML.
    const looksHtml = t.startsWith('<') || /<(html|body|head|h1|center)\b/i.test(t.slice(0, 200));
    if (looksHtml) return statusFallback;
    return t.length > 240 ? t.slice(0, 240) + '…' : t;
  } catch (_) {
    return statusFallback;
  }
}

// ── SEC-25 (2026-06-11) — DO NOT persist tokens to sessionStorage. ──
// This function is kept as a no-op (callers downstream still invoke it)
// to minimise the diff against every caller path; it now exists ONLY to
// defensively wipe any sessionStorage residue from prior code paths.
// If you find yourself wanting to "re-enable" persistence — DON'T. The
// HttpOnly cookie (`agentlets_session`) is the only auth-persistence
// surface the platform supports. Any new persistence requirement must
// go through the cookie path so XSS cannot exfiltrate the secret.
function _persistTokens() {
  try {
    sessionStorage.removeItem('_authToken');
    sessionStorage.removeItem('_refreshToken');
    sessionStorage.removeItem('_tokenExpiresAt');
  } catch (_) { /* sessionStorage unavailable — ignore */ }
}

// Persist user/session/memberships so AuthContext can hydrate after a full-page redirect
function _persistSession(user, session, memberships) {
  if (user) {
    sessionStorage.setItem('_authUser', JSON.stringify(user));
  } else {
    sessionStorage.removeItem('_authUser');
  }
  if (session) {
    sessionStorage.setItem('_authSession', JSON.stringify(session));
  } else {
    sessionStorage.removeItem('_authSession');
  }
  if (memberships) {
    sessionStorage.setItem('_authMemberships', JSON.stringify(memberships));
  } else {
    sessionStorage.removeItem('_authMemberships');
  }
}

/**
 * Attempt to restore a session from sessionStorage after a full-page redirect.
 * Returns { user, session, memberships } if user metadata is cached, or null.
 *
 * <p>Post-SEC-25 (2026-06-11): no longer gates on {@code _authToken}.
 * Tokens are not persisted (the HttpOnly cookie handles auth) so after a
 * full-page reload {@code _authToken} is always null even for a fully
 * authenticated session. The cached user/session/memberships objects in
 * sessionStorage ARE still useful for instant UI hydration without a
 * round-trip — the caller is expected to follow up with
 * {@link revalidateSession} (or a /auth/session call) to confirm with
 * the server whether the cookie is still valid. If it isn't, the caller
 * tears down the cached metadata and redirects to login.
 */
export const restoreSession = () => {
  try {
    const userStr = sessionStorage.getItem('_authUser');
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    const session = JSON.parse(sessionStorage.getItem('_authSession') || 'null');
    const memberships = JSON.parse(sessionStorage.getItem('_authMemberships') || '[]');
    return { user, session, memberships };
  } catch (e) {
    console.warn('[auth] Failed to restore session from sessionStorage:', e);
    return null;
  }
};

/**
 * DEF-EXEC-002 (Bug A) — Server-authoritative session refresh.
 *
 * Calls `GET /api/v1/auth/session`, which returns the role/tenant/org/
 * permissions extracted from the LIVE JWT. The previous behaviour was to
 * rehydrate `session.role` from `sessionStorage` on app mount, which could
 * drift from what the JWT actually claims — e.g. a user who logged in
 * before a role change, or whose `app_users.role` differs from the
 * resolved `user_org_roles` membership the backend uses for JWT issuance.
 * The drift surfaced as "left-nav shows Super Admin" while every protected
 * write was being rejected as "Tenant Admin does not have agent.delete".
 *
 * Returns { role, tenantId, orgId, userId, permissions } on success, or
 * null if the token is missing/expired (caller should treat that as a
 * sign-out trigger).
 */
export const revalidateSession = async () => {
  // SEC-25 — post-cleanup we authenticate via the HttpOnly cookie. The
  // in-memory _authToken IS NULL after a page reload (it's no longer
  // hydrated from sessionStorage). Send the cookie via credentials:
  // 'include' AND, when present, the bearer header as a defensive
  // fallback for any deployment where the cookie isn't reaching the
  // backend (e.g. cross-origin dev setups without proper CORS).
  try {
    const headers = {};
    if (_authToken) headers['Authorization'] = `Bearer ${_authToken}`;
    const res = await fetch(API_URL + '/api/v1/auth/session', {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body?.authenticated) return null;
    return {
      userId: body.userId || null,
      role: body.role || null,
      tenantId: body.tenantId || null,
      orgId: body.orgId || null,
      // RC-137a — surface customerId too. The BE /session now returns it
      // (JWT-sourced, like tenantId/orgId), so dropping it here would
      // re-introduce the same drift class: AuthContext.session.customerId
      // could fall stale after switchTenant → no consumer of
      // session.customerId (e.g. the scope-picker visibility gate) would
      // re-render correctly on page reload.
      customerId: body.customerId || null,
      // RC-137f — display names for the active scope chip. Derived
      // server-side from the JWT-active ids (tenantName from tid,
      // orgName from oid, customerName from cid). Without these the
      // chip falls back to a UUID-slice for PA after switchTenant.
      tenantName: body.tenantName || null,
      orgName: body.orgName || null,
      customerName: body.customerName || null,
      permissions: Array.isArray(body.permissions) ? body.permissions : null,
    };
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[auth] revalidateSession failed:', e);
    }
    return null;
  }
};

export const configure = (opts) => {
  if (opts.onSessionExpired) _onSessionExpired = opts.onSessionExpired;
  if (opts.onTokenRefreshed) _onTokenRefreshed = opts.onTokenRefreshed;
};

const checkDevMode = async () => {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/health`, { signal: AbortSignal.timeout(3000) });
    if (res.ok) {
      _devMode = false;
      return false;
    }
    _devMode = true;
    console.info('[auth] Backend unreachable, running in dev mode');
    return true;
  } catch (e) {
    _devMode = true;
    console.info('[auth] Backend unreachable, running in dev mode');
    return true;
  }
};

export const isDevMode = () => _devMode;
export { checkDevMode };

// ---------------------------------------------------------------
// Login (first factor — email + password)
// ---------------------------------------------------------------
export const smartLogin = async (email, password) => {
  const isDev = await checkDevMode();

  if (isDev) {
    const acct = DEV_ACCOUNTS.find((a) => a.email === email);
    if (!acct) return { ok: false, error: 'Account not found in dev mode' };
    if (password !== 'demo') return { ok: false, error: 'Invalid password (use "demo" in dev mode)' };

    _authToken = `dev_${acct.id}_${Date.now()}`;
    _tokenExpiresAt = new Date(Date.now() + 7200000);
    _currentEmail = email;

    return {
      ok: true,
      mfaRequired: true,
      mfaMethod: 'totp',
      user: acct,
    };
  }

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    // Parse defensively: a rejected request (e.g. a CORS 403 whose body is plain text, not JSON)
    // is not parseable, and calling res.json() before the res.ok check turned every such failure
    // into a misleading "Network error". Keep {} on a non-JSON body and surface the real status.
    let data = {};
    try { data = await res.json(); } catch (_) { /* non-JSON error body — leave data as {} */ }
    if (!res.ok) return { ok: false, error: data.error || `Login failed (HTTP ${res.status})` };

    _currentEmail = email;

    if (data.mfaRequired) {
      // Store MFA token for the verification step
      _authToken = data.mfaToken;
      return {
        ok: true,
        mfaRequired: true,
        mfaMethod: data.mfaMethod || 'totp',
        email: data.email,
      };
    }

    // No MFA — full auth complete
    _authToken = data.accessToken;
    _refreshToken = data.refreshToken;
    _tokenExpiresAt = new Date(Date.now() + 28800000);
    _persistTokens();
    _persistSession(data.user, data.session, data.memberships);

    return {
      ok: true,
      mfaRequired: false,
      user: data.user,
      memberships: data.memberships,
      session: data.session,
    };
  } catch (e) {
    console.error('[auth] Login error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// MFA Verification (second factor)
// ---------------------------------------------------------------
export const smartVerifyMfa = async (code, method) => {
  const isDev = await checkDevMode();

  if (isDev) {
    if (code !== '000000') return { ok: false, error: 'Invalid code (use "000000" in dev mode)' };

    const acct = DEV_ACCOUNTS.find((a) => a.email === _currentEmail) || DEV_ACCOUNTS[0];
    return {
      ok: true,
      user: acct,
      role: acct.role,
      memberships: [{ orgId: DEFAULT_ORG_UUID, orgName: 'Default Organization', tenantId: DEFAULT_TENANT_UUID, tenantName: 'Default Tenant', role: acct.role, isDefault: true }],
      session: {
        userId: acct.id,
        role: acct.role,
        tenantId: DEFAULT_TENANT_UUID,
        orgId: DEFAULT_ORG_UUID,
        mfaVerified: true,
      },
    };
  }

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: _currentEmail, code, method }),
    });

    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Verification failed' };

    _authToken = data.accessToken;
    _refreshToken = data.refreshToken;
    _tokenExpiresAt = new Date(Date.now() + 28800000);
    _persistTokens();
    _persistSession(data.user, data.session, data.memberships);

    return {
      ok: true,
      user: data.user,
      role: data.user?.role,
      memberships: data.memberships,
      session: data.session,
    };
  } catch (e) {
    console.error('[auth] MFA error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// SSO Initiate
// ---------------------------------------------------------------
export const smartSsoInitiate = async (provider) => {
  const isDev = await checkDevMode();

  if (isDev) {
    const user = DEV_ACCOUNTS[0];
    _currentEmail = user.email;
    _authToken = `dev_sso_${Date.now()}`;
    _tokenExpiresAt = new Date(Date.now() + 7200000);
    return {
      ok: true,
      devFallback: true,
      mfaRequired: true,
      mfaMethod: 'totp',
      user,
    };
  }

  try {
    // `provider` here is the provider id resolved by Home Realm Discovery from the user's work email.
    const res = await fetch(`${API_URL}/api/v1/auth/sso/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerId: provider }),
    });

    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'SSO initiation failed' };

    return {
      ok: true,
      authorizationUrl: data.authorizationUrl,
    };
  } catch (e) {
    console.error('[auth] SSO initiate error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// SSO Home Realm Discovery
// ---------------------------------------------------------------
// Resolves which sign-in provider(s) serve a work email's domain.
// Returns [{ id, kind, displayName }]; an empty list means the
// domain has no single sign-on configured (fall back to password).
export const discoverSsoProviders = async (email) => {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/sso/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Unable to check single sign-on', providers: [] };
    return { ok: true, providers: Array.isArray(data.providers) ? data.providers : [] };
  } catch (e) {
    console.error('[auth] SSO discover error:', e);
    return { ok: false, error: 'Network error', providers: [] };
  }
};

// ---------------------------------------------------------------
// SSO Callback
// ---------------------------------------------------------------
export const ssoCallback = async (provider, code, state) => {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/sso/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, code, state }),
    });

    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'SSO callback failed' };

    if (data.mfaRequired) {
      _authToken = data.mfaToken;
      _currentEmail = data.email;
      return { ok: true, mfaRequired: true, mfaMethod: data.mfaMethod || 'totp' };
    }

    _authToken = data.accessToken;
    _refreshToken = data.refreshToken;
    _tokenExpiresAt = new Date(Date.now() + 28800000);
    _persistTokens();
    _persistSession(data.user, data.session, data.memberships);

    return {
      ok: true,
      user: data.user,
      memberships: data.memberships,
      session: data.session,
      mfaRequired: false,
    };
  } catch (e) {
    console.error('[auth] SSO callback error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// SSO Popup
// ---------------------------------------------------------------
export const openSsoPopup = (authorizationUrl, callback) => {
  const width = 500;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    authorizationUrl, 'sso-popup',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );

  if (!popup) {
    console.error('[auth] Popup blocked');
    return;
  }

  const pollInterval = setInterval(() => {
    try {
      if (popup.closed) { clearInterval(pollInterval); return; }
      const urlParams = new URL(popup.location.href);
      const code = urlParams.searchParams.get('code');
      const state = urlParams.searchParams.get('state');
      if (code && state) {
        popup.close();
        clearInterval(pollInterval);
        callback(code, state);
      }
    } catch (e) { /* cross-origin, keep polling */ }
  }, 500);

  setTimeout(() => {
    if (!popup.closed) popup.close();
    clearInterval(pollInterval);
  }, 600000);
};

// ---------------------------------------------------------------
// Register
// ---------------------------------------------------------------
export const registerUser = async (firstName, lastName, email, password, mobileNumber) => {
  const isDev = await checkDevMode();

  if (isDev) {
    return { ok: true, message: 'Registration successful (dev mode)' };
  }

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, email, password, mobileNumber }),
    });

    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Registration failed' };

    return { ok: true, ...data };
  } catch (e) {
    console.error('[auth] Register error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// Forgot Password
// ---------------------------------------------------------------
export const requestForgotPassword = async (email) => {
  const isDev = await checkDevMode();

  if (isDev) {
    return { ok: true, message: 'Reset link sent (dev mode)' };
  }

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await res.json();
    return { ok: true, ...data };
  } catch (e) {
    console.error('[auth] Forgot password error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// Reset Password
// ---------------------------------------------------------------
export const resetPassword = async (token, newPassword) => {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });

    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Reset failed' };
    return { ok: true, ...data };
  } catch (e) {
    console.error('[auth] Reset password error:', e);
    return { ok: false, error: 'Network error' };
  }
};

// ---------------------------------------------------------------
// Token management
// ---------------------------------------------------------------
export const getAccessToken = () => _authToken;
export const setAccessToken = (token) => { _authToken = token; _persistTokens(); };

export const clearTokens = () => {
  _authToken = null;
  _refreshToken = null;
  _tokenExpiresAt = null;
  _currentEmail = null;
  _persistTokens();
  _persistSession(null, null, null);
};

export const logout = async () => {
  /* DEF-AUTH-LOGOUT-FE-SKIPS-SERVER-001 (P0 SECURITY) — the prior
   * implementation guarded `if (!_devMode && _authToken)` before
   * POSTing /logout. Because G409 routes the access token through an
   * HttpOnly cookie (`agentlets_session`) that JavaScript can never
   * touch, `_authToken` is null on the canonical cookie-auth path,
   * which meant the server-side /logout was never called when a user
   * clicked Logout in the UI. The HttpOnly cookie + per-user iat
   * watermark stayed live for the full 8h cookie max-age, so anyone
   * with access to the browser could resume the session by hitting
   * any authenticated endpoint. Empirically replicated 2026-05-23:
   * a manual POST /logout from the console returned 200, cleared both
   * cookies, and made every subsequent API call 403 — proving the
   * server side works correctly. The bug was strictly the FE skipping
   * the call.
   *
   * Fix:
   *   1. Drop the `_authToken` guard — always POST /logout. Cookie
   *      auth is the primary path; the bearer header is a fallback
   *      that should accompany the cookie, not gate it.
   *   2. Add `credentials: 'include'` so the HttpOnly cookie is sent
   *      and the server can identify the user to bump the watermark.
   *   3. Wrap in try/finally so a network error never prevents the
   *      local clearTokens() — better to clear FE state than to leak
   *      stale token references on a failed-network logout.
   *   4. Send the CSRF double-submit header on the mutating call so
   *      the request actually passes the CsrfHeaderFilter (logout is
   *      a mutating action). */
  const csrf = (typeof document !== 'undefined' && document.cookie)
    ? (document.cookie.split('csrf_token=')[1] || '').split(';')[0]
    : '';
  try {
    if (!_devMode) {
      const headers = { 'Content-Type': 'application/json' };
      if (_authToken) headers.Authorization = `Bearer ${_authToken}`;
      if (csrf) headers['X-CSRF-Token'] = csrf;
      await fetch(`${API_URL}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include', // critical for cookie-auth path
        headers,
      });
    }
  } catch (e) {
    console.error('[auth] Logout error:', e);
  } finally {
    clearTokens();
  }
};

/**
 * G410 — Membership switch. Calls POST /api/v1/auth/switch-membership with
 * the chosen membershipId, then replaces the in-memory + sessionStorage
 * tokens + user/session/memberships with the response. Caller is expected
 * to full-page reload so all React-Query caches refetch under the new scope.
 *
 * @param {string} membershipId — the user_org_roles.id to switch to
 * @returns {{ ok: boolean, error?: string, session?: object }}
 */
/**
 * G409c / RC-91 — double-submit CSRF. Every MUTATING auth call (switch-*, logout)
 * must echo the non-HttpOnly csrf_token cookie in the X-CSRF-Token header, or the
 * backend CsrfHeaderFilter rejects with 403 ("cookie present, header missing")
 * BEFORE the request reaches its controller. The switch-* helpers below predate
 * CSRF enforcement and were missed when it landed (only apiFetch + logout were
 * updated), which surfaced as a bogus "Not authorized to switch customer". This
 * centralizes the header so the three switch flows can't drift from it again.
 * Decodes to match what apiFetch sends (the proven-working mutating path).
 */
function _withCsrf(headers) {
  try {
    const raw = (typeof document !== 'undefined' && document.cookie)
      ? (document.cookie.split('csrf_token=')[1] || '').split(';')[0] : null;
    if (raw) headers['X-CSRF-Token'] = decodeURIComponent(raw);
  } catch (_) { /* best-effort — server returns 403 if truly required + absent */ }
  return headers;
}

export const switchMembership = async (membershipId) => {
  if (!membershipId) return { ok: false, error: 'membershipId is required' };
  // SEC-25 — drop the `!_authToken` gate. After a page reload _authToken
  // is null but the HttpOnly cookie still authenticates. If the user
  // really isn't auth'd, the server returns 401 and we surface it cleanly.
  if (_devMode) return { ok: false, error: 'Not authenticated' };
  try {
    const headers = _withCsrf({ 'Content-Type': 'application/json' });
    if (_authToken) headers.Authorization = `Bearer ${_authToken}`;
    const res = await fetch(`${API_URL}/api/v1/auth/switch-membership`, {
      method: 'POST',
      credentials: 'include',  // SEC-25 — cookie path is now canonical
      headers,
      body: JSON.stringify({ membershipId }),
    });
    if (!res.ok) {
      // 404 means the caller doesn't own this membership (RBAC §7.5 row-existence privacy)
      if (res.status === 404) return { ok: false, error: 'Membership not found' };
      // G590 — extract a sanitized error (handles nginx HTML 502 etc.)
      return { ok: false, error: await _extractErrorMessage(res, 'Switch failed') };
    }
    const data = await res.json();
    _authToken = data.accessToken;
    _refreshToken = data.refreshToken || _refreshToken;
    _tokenExpiresAt = new Date(Date.now() + 28800000);
    _persistTokens();
    _persistSession(data.user, data.session, data.memberships);
    if (_onTokenRefreshed) _onTokenRefreshed(_authToken);
    return { ok: true, session: data.session, memberships: data.memberships };
  } catch (e) {
    console.error('[auth] switchMembership error:', e);
    return { ok: false, error: 'Network error' };
  }
};

/**
 * V191 / Phase-4 — atomic switch-instance flow.
 *
 * Mirrors {@link switchMembership} but for moving the caller's session
 * scope to a different Instance under the same Tenant. The backend
 * (AuthController#switchInstance) validates that the target Instance
 * belongs to the caller's current Tenant + the caller's effective role
 * grants instance.switch, then re-issues the JWT with a new `iid` claim
 * and blacklists the previous token (G408 pattern).
 *
 * @param {string} instanceId — the instances.id to switch to (UUID)
 * @returns {{ ok: boolean, error?: string, session?: object }}
 */
export const switchInstance = async (instanceId) => {
  if (!instanceId) return { ok: false, error: 'instanceId is required' };
  // SEC-25 — drop the `!_authToken` gate (cookie path covers it).
  if (_devMode) return { ok: false, error: 'Not authenticated' };
  try {
    const headers = _withCsrf({ 'Content-Type': 'application/json' });
    if (_authToken) headers.Authorization = `Bearer ${_authToken}`;
    const res = await fetch(`${API_URL}/api/v1/auth/switch-instance`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ instanceId }),
    });
    if (!res.ok) {
      if (res.status === 404) return { ok: false, error: 'Instance not found' };
      if (res.status === 403) return { ok: false, error: 'Not authorized to switch to this Instance' };
      // G590 — sanitize the error body (don't leak nginx HTML pages).
      return { ok: false, error: await _extractErrorMessage(res, 'Switch failed') };
    }
    const data = await res.json();
    // Phase-4 response carries the same shape as login + switch-membership:
    // { accessToken, refreshToken?, user, session, memberships?, csrfToken? }
    // session.instanceId is the new claim we care about.
    if (data.accessToken) {
      _authToken = data.accessToken;
      _refreshToken = data.refreshToken || _refreshToken;
      _tokenExpiresAt = new Date(Date.now() + 28800000);
      _persistTokens();
    }
    _persistSession(data.user, data.session, data.memberships);
    if (_onTokenRefreshed && data.accessToken) _onTokenRefreshed(_authToken);
    return { ok: true, session: data.session };
  } catch (e) {
    console.error('[auth] switchInstance error:', e);
    return { ok: false, error: 'Network error' };
  }
};

/**
 * V208 / Phase D-2 — atomic switch-customer flow.
 *
 * G583 — mirrors {@link switchInstance} for the customer-scope switch.
 * Earlier the CustomersPageV2 page called the raw POST helper in
 * services/api.js and threw the response away, so the new JWT was never
 * saved to localStorage. The next request after window.location.href
 * re-loaded sent the STALE token, JwtAuthFilter spotted that its iat
 * pre-dated the just-recorded blacklist watermark, and the page bounced
 * the user to /login?reason=role_changed. Persisting tokens + session
 * here (same pattern as switch-membership / switch-instance) keeps the
 * caller signed in across the scope change.
 *
 * @param {string} customerId — the customers.id to switch the session into (UUID)
 * @returns {{ ok: boolean, error?: string, session?: object }}
 */
export const switchCustomer = async (customerId) => {
  if (!customerId) return { ok: false, error: 'customerId is required' };
  // SEC-25 — drop the `!_authToken` gate (cookie path covers it).
  if (_devMode) return { ok: false, error: 'Not authenticated' };
  try {
    const headers = _withCsrf({ 'Content-Type': 'application/json' });
    if (_authToken) headers.Authorization = `Bearer ${_authToken}`;
    const res = await fetch(`${API_URL}/api/v1/auth/switch-customer`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ customerId }),
    });
    if (!res.ok) {
      if (res.status === 404) return { ok: false, error: 'Customer not found' };
      if (res.status === 403) return { ok: false, error: 'Not authorized to switch customer' };
      // G590 — sanitize the error body (don't leak nginx HTML pages).
      return { ok: false, error: await _extractErrorMessage(res, 'Switch failed') };
    }
    const data = await res.json();
    if (data.accessToken) {
      _authToken = data.accessToken;
      _refreshToken = data.refreshToken || _refreshToken;
      _tokenExpiresAt = new Date(Date.now() + 28800000);
      _persistTokens();
    }
    _persistSession(data.user, data.session, data.memberships);
    if (_onTokenRefreshed && data.accessToken) _onTokenRefreshed(_authToken);
    return { ok: true, session: data.session };
  } catch (e) {
    console.error('[auth] switchCustomer error:', e);
    return { ok: false, error: 'Network error' };
  }
};

/**
 * RC-137a — Pin the Platform Admin into a specific tenant.
 *
 * Companion to {@link switchCustomer}. Same JWT persistence pattern
 * (otherwise the next request fires with a stale token and JwtAuthFilter
 * 401s on the watermark mismatch). On success the caller should reload
 * so every page re-resolves under the new tenantId.
 *
 * @param {string} tenantId — the tenants.id to pin the session to (UUID)
 * @returns {{ ok: boolean, error?: string, session?: object }}
 */
export const switchTenant = async (tenantId) => {
  if (!tenantId) return { ok: false, error: 'tenantId is required' };
  if (_devMode) return { ok: false, error: 'Not authenticated' };
  try {
    const headers = _withCsrf({ 'Content-Type': 'application/json' });
    if (_authToken) headers.Authorization = `Bearer ${_authToken}`;
    const res = await fetch(`${API_URL}/api/v1/auth/switch-tenant`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ tenantId }),
    });
    if (!res.ok) {
      if (res.status === 404) return { ok: false, error: 'Tenant not found' };
      if (res.status === 403) return { ok: false, error: 'Not authorized to pin tenant' };
      return { ok: false, error: await _extractErrorMessage(res, 'Tenant pin failed') };
    }
    const data = await res.json();
    if (data.accessToken) {
      _authToken = data.accessToken;
      _refreshToken = data.refreshToken || _refreshToken;
      _tokenExpiresAt = new Date(Date.now() + 28800000);
      _persistTokens();
    }
    _persistSession(data.user, data.session, data.memberships);
    if (_onTokenRefreshed && data.accessToken) _onTokenRefreshed(_authToken);
    return { ok: true, session: data.session };
  } catch (e) {
    console.error('[auth] switchTenant error:', e);
    return { ok: false, error: 'Network error' };
  }
};

/**
 * Single-flight wrapper around the refresh call. At the 8h access-token expiry
 * boundary there are multiple independent callers — every in-flight apiFetch's
 * isTokenExpired() pre-check, every parallel request's 401-retry, AND the
 * form-tasks SSE bus `recover` hook. Without de-duplication they all POST
 * /api/v1/auth/token/refresh at once; the backend's PER-IP rate limiter
 * (tryAcquire("token-refresh", clientIp)) then rejects the losers, the FE
 * reads any non-OK refresh as terminal → _onSessionExpired() + clearTokens()
 * → a spurious hard logout (and the SSE side keeps hard-closing → churn).
 *
 * Collapsing concurrent callers onto one shared promise means exactly one
 * network refresh per expiry; every caller resolves with the same fresh token.
 * The guard is released once the call settles so the next genuine expiry
 * refreshes again.
 */
export const refreshAccessToken = () => {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = _doRefreshAccessToken().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
};

const _doRefreshAccessToken = async () => {
  // Do NOT bail when the in-memory refresh token is absent. Post-SEC-25 the refresh token
  // also rides in an HttpOnly cookie (agentlets_refresh) that survives a page reload (when
  // sessionStorage — and thus _refreshToken — is cleared). With credentials:'include' the
  // server reads it from that cookie, so the SPA renews silently after reload+expiry
  // instead of hard-logging-out. Header still used when the in-memory token is present.
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (_refreshToken) headers.Authorization = `Bearer ${_refreshToken}`;
    const res = await fetch(`${API_URL}/api/v1/auth/token/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });

    if (!res.ok) {
      if (_onSessionExpired) _onSessionExpired('Token refresh failed');
      clearTokens();
      return null;
    }

    const data = await res.json();
    _authToken = data.accessToken;
    _refreshToken = data.refreshToken || _refreshToken;
    _tokenExpiresAt = new Date(Date.now() + 28800000);
    _persistTokens();

    if (_onTokenRefreshed) _onTokenRefreshed(_authToken);
    return _authToken;
  } catch (e) {
    console.error('[auth] Token refresh error:', e);
    if (_onSessionExpired) _onSessionExpired('Token refresh error');
    clearTokens();
    return null;
  }
};

export const isTokenExpired = () => {
  if (!_tokenExpiresAt) return false;
  return new Date() > _tokenExpiresAt;
};

// ---------------------------------------------------------------
// Invitation Registration (token-based)
// ---------------------------------------------------------------
// Helper: decode a client-side base64 invite token (generated by sendInvitation)
const decodeClientToken = (token) => {
  try {
    const decoded = JSON.parse(atob(token));
    // Check expiration — token contains { userId, email, exp } or full invite data with expiresAt
    const expiry = decoded.exp || decoded.expiresAt;
    if (expiry && new Date(expiry) < new Date()) {
      return { ok: false, error: 'Invitation has expired' };
    }
    return { ok: true, data: decoded };
  } catch (e) {
    return { ok: false, error: 'Invalid invitation token' };
  }
};

export const validateInviteToken = async (token) => {
  // Try backend first (server-opaque tokens issued by InvitationService).
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/invite/validate?token=${encodeURIComponent(token)}`, {
      signal: AbortSignal.timeout(5000),
    });
    const data = await res.json().catch(() => null);
    if (res.ok) {
      return { ok: true, data: data || {} };
    }
    // The backend evaluated the token and rejected it (expired / already used /
    // unknown). Surface ITS specific message instead of the generic base64
    // fallback below — otherwise every rejection reads "Invalid invitation token".
    if (data && data.error) {
      return { ok: false, error: data.error };
    }
  } catch (_) { /* endpoint unreachable — fall through to legacy client-token decode */ }

  // Legacy fallback: decode a client-side base64 token (older invite flow).
  return decodeClientToken(token);
};

export const registerWithInvite = async (token, { password }) => {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/invite/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
      signal: AbortSignal.timeout(10000),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true, ...data };
    }
    return { ok: false, error: data.error || 'Registration failed' };
  } catch (err) {
    console.error('registerWithInvite error:', err);
    return { ok: false, error: 'Network error during registration. Please try again.' };
  }
};

export { DEV_ACCOUNTS };
